import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useCreateSession } from '@/queries/useSessions';
import { useSessionStore } from '@/store/sessionStore';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Voice Route Component
 * Handles QR code → session creation → voice interface flow
 * URL format: /voice?branchId=xxx&tableId=xxx or /voice?branchId=xxx&tableNumber=xxx
 */
export default function Voice() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useSessionStore();

  // Extract URL parameters
  const branchId = searchParams.get('branchId');
  const tableId = searchParams.get('tableId');
  const tableNumber = searchParams.get('tableNumber');

  // Use tableNumber or tableId (both supported)
  const table = tableNumber || tableId;

  // Create session mutation
  const {
    mutate: createSession,
    isPending: isCreating,
    isError,
    error,
    isSuccess,
  } = useCreateSession();

  // Auto-create session on component mount
  useEffect(() => {
    if (!branchId) {
      return; // Show error state (handled below)
    }

    // Create session automatically
    createSession(
      {
        branchId,
        tableNumber: table || undefined,
        orderType: 'dine-in',
      },
      {
        onSuccess: (data) => {
          // Store session in Zustand + localStorage
          setSession({
            sessionId: data.sessionId,
            orderId: data.orderId, // Order ID from backend (draft order created with session)
            token: data.token,
            branchId: data.branchId,
            expiresAt: data.expiresAt,
          });

          // Navigate to menu page to start ordering
          // TODO: Navigate to voice interface when implemented (Story 4.2)
          navigate('/menu', { replace: true });
        },
        onError: (err) => {
          console.error('Session creation failed:', err);
        },
      }
    );
  }, [branchId, table, createSession, setSession, navigate]);

  // Missing parameters error
  if (!branchId) {
    return (
      <div className="min-h-screen flex flex-col p-4 bg-background">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md w-full space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Invalid QR Code</AlertTitle>
              <AlertDescription>
                The QR code you scanned is invalid or missing required information.
                Please scan a valid QR code or contact restaurant staff for assistance.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => window.location.reload()}
              className="w-full"
              variant="outline"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state - session creation in progress
  if (isCreating) {
    return (
      <div className="min-h-screen flex flex-col p-4 bg-background">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-lg font-medium text-foreground">
              Setting up your order...
            </p>
            <p className="text-sm text-muted-foreground">
              Please wait a moment
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state - session creation failed
  if (isError) {
    return (
      <div className="min-h-screen flex flex-col p-4 bg-background">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md w-full space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to Start Session</AlertTitle>
              <AlertDescription>
                {error instanceof Error
                  ? error.message
                  : 'An error occurred while setting up your session. Please try again or contact restaurant staff.'}
              </AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Button
                onClick={() => window.location.reload()}
                className="flex-1"
                variant="outline"
              >
                Retry
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="flex-1"
              >
                Go Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state - session created, ready for voice interface
  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col p-4 bg-background">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md w-full space-y-4 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-2xl font-bold text-foreground">
              Session Ready!
            </h1>
            <p className="text-muted-foreground">
              Your order session has been created successfully.
            </p>
            <p className="text-sm text-muted-foreground">
              Branch ID: {branchId}
              {table && ` • Table: ${table}`}
            </p>
            <div className="pt-4">
              <p className="text-sm text-muted-foreground italic">
                Voice interface will be displayed here (Story 4.2)
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
